import { Button, Input, Segmented, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { UnitSystem } from './types';
import { UNIT_SUFFIX } from './types';

const { Text } = Typography;

type InputMode = 'radius' | 'diameter';

export interface InputPanelProps {
	unit: UnitSystem;
	onUnitChange: (u: UnitSystem) => void;
	inputMode: InputMode;
	onInputModeChange: (m: InputMode) => void;
	chordStr: string;
	onChordStrChange: (s: string) => void;
	onChordFocus: () => void;
	onChordBlur: () => void;
	radiusOrDiameterStr: string;
	onRadiusOrDiameterStrChange: (s: string) => void;
	onRadiusDiaFocus: () => void;
	onRadiusDiaBlur: () => void;
	tolPlusStr: string;
	onTolPlusStrChange: (s: string) => void;
	tolMinusStr: string;
	onTolMinusStrChange: (s: string) => void;
	/** Field-level errors shown inline under each input. */
	chordError: string | null;
	radiusError: string | null;
	tolPlusError: string | null;
	tolMinusError: string | null;
	/** Cross-field geometry error (e.g., chord exceeds diameter). */
	geometryError: string | null;
	/** Cross-field tolerance error (e.g., minus tolerance too large). */
	toleranceError: string | null;
	onReset?: () => void;
	canReset?: boolean;
}

function FieldError({ message }: { message: string | null }) {
	if (!message) return null;
	return (
		<Text type="danger" className="text-xs block mt-1">
			{message}
		</Text>
	);
}

export default function InputPanel({
	unit,
	onUnitChange,
	inputMode,
	onInputModeChange,
	chordStr,
	onChordStrChange,
	onChordFocus,
	onChordBlur,
	radiusOrDiameterStr,
	onRadiusOrDiameterStrChange,
	onRadiusDiaFocus,
	onRadiusDiaBlur,
	tolPlusStr,
	onTolPlusStrChange,
	tolMinusStr,
	onTolMinusStrChange,
	chordError,
	radiusError,
	tolPlusError,
	tolMinusError,
	geometryError,
	toleranceError,
	onReset,
	canReset,
}: InputPanelProps) {
	const suffix = UNIT_SUFFIX[unit];
	const hasMinus = tolMinusStr.trim() !== '';
	const plusLabel = hasMinus ? '+' : '+/-';
	const symmetric = !hasMinus;
	const chordPlaceholder = unit === 'inches' ? 'e.g., 3.500' : 'e.g., 88.900';
	const rPlaceholder = unit === 'inches' ? 'e.g., 24.000' : 'e.g., 609.600';
	const tolPlaceholder = unit === 'inches' ? '0.005' : '0.13';

	const chordHasGeoError = !!geometryError && !chordError;
	const radiusHasGeoError = !!geometryError && !radiusError;

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-3">
				<Text strong>Inputs</Text>
				{onReset ? (
					<Tooltip title="Clear all inputs">
						<Button
							type="text"
							size="small"
							icon={<ReloadOutlined />}
							onClick={onReset}
							disabled={!canReset}
						>
							Reset
						</Button>
					</Tooltip>
				) : null}
			</div>
			<div>
				<Text strong className="mb-1 block">
					Units
				</Text>
				<Segmented
					value={unit}
					onChange={(v) => onUnitChange(v as UnitSystem)}
					options={[
						{ label: 'Inches', value: 'inches' },
						{ label: 'Millimeters', value: 'mm' },
					]}
				/>
			</div>

			<div>
				<Text strong className="mb-1 block">
					Chord Length (c)
				</Text>
				<Input
					inputMode="decimal"
					value={chordStr}
					onChange={(e) => onChordStrChange(e.target.value)}
					onFocus={onChordFocus}
					onBlur={onChordBlur}
					placeholder={chordPlaceholder}
					addonAfter={suffix}
					status={chordError || chordHasGeoError ? 'error' : undefined}
					aria-invalid={!!chordError || undefined}
				/>
				<FieldError message={chordError} />
			</div>

			<div>
				<Text strong className="mb-1 block">
					Dimension
				</Text>
				<Segmented
					className="mb-2"
					value={inputMode}
					onChange={(v) => onInputModeChange(v as InputMode)}
					options={[
						{ label: 'Radius (R)', value: 'radius' },
						{ label: 'Diameter (D)', value: 'diameter' },
					]}
				/>
				<Input
					inputMode="decimal"
					value={radiusOrDiameterStr}
					onChange={(e) => onRadiusOrDiameterStrChange(e.target.value)}
					onFocus={onRadiusDiaFocus}
					onBlur={onRadiusDiaBlur}
					placeholder={rPlaceholder}
					addonAfter={suffix}
					status={radiusError || radiusHasGeoError ? 'error' : undefined}
					aria-invalid={!!radiusError || undefined}
				/>
				<FieldError message={radiusError} />
			</div>

			<div>
				<Text strong className="mb-1 block">
					Radius Tolerance (optional)
				</Text>
				<div className="grid grid-cols-2 gap-2">
					<div>
						<Text type="secondary" className="text-xs block mb-0.5">
							{plusLabel}
						</Text>
						<Input
							inputMode="decimal"
							value={tolPlusStr}
							onChange={(e) => onTolPlusStrChange(e.target.value)}
							placeholder={tolPlaceholder}
							addonAfter={suffix}
							status={
								tolPlusError || toleranceError ? 'error' : undefined
							}
							aria-invalid={!!tolPlusError || undefined}
						/>
						<FieldError message={tolPlusError} />
					</div>
					<div>
						<Text type="secondary" className="text-xs block mb-0.5">
							−
						</Text>
						<Input
							inputMode="decimal"
							value={tolMinusStr}
							onChange={(e) => onTolMinusStrChange(e.target.value)}
							placeholder={tolPlaceholder}
							addonAfter={suffix}
							status={
								tolMinusError || toleranceError ? 'error' : undefined
							}
							aria-invalid={!!tolMinusError || undefined}
						/>
						<FieldError message={tolMinusError} />
					</div>
				</div>
				<Text type="secondary" className="text-xs block mt-1">
					{symmetric ? 'Symmetric tolerance' : 'Asymmetric tolerance'}
				</Text>
				{toleranceError ? (
					<Text type="danger" className="text-xs block mt-1">
						{toleranceError}
					</Text>
				) : null}
			</div>

			{geometryError ? (
				<Text type="danger" className="text-sm block">
					{geometryError}
				</Text>
			) : null}
		</div>
	);
}
